// components/tasks/TaskDetailDrawer.tsx
// Asana-style right-side task detail drawer. Opened from TaskTrackingPage when a
// task card/row is clicked. Holds inline title editing, a description editor,
// a meta row (status / assignee / due date / priority), an attachments section,
// and a comment thread. Every field saves immediately to the backend and the
// parent list is kept in sync via onTaskUpdated.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Checkbox,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Skeleton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  SelectChangeEvent,
  CircularProgress,
  Popper,
  Paper,
  MenuList,
  ClickAwayListener,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import {
  RECUR_UNITS,
  clampInterval,
  composeRule,
  recurrenceLabel,
  ruleToControl,
  nextOccurrence,
  RecurUnit,
} from './recurrence';

// --- Shared types (single source of truth; re-imported by TaskTrackingPage) ---
export interface Task {
  id: number;
  title: string;
  description: string;
  platform: string | null;
  status: 'pending' | 'completed';
  assignee_id?: number | null;
  assignee_name?: string;
  assignee_email?: string;
  priority: 'low' | 'medium' | 'high';
  tags: string[];
  images?: string[];
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  completed_by?: number | null;
  completed_by_name?: string;
  comment_count?: number;
  due_date?: string | null;
  link_type?: 'order' | 'customer' | null;
  link_id?: string | null;
  source?: string | null;
  board_section?: string; // 'todo' | 'doing' | 'waiting' | 'review' (default 'todo')
  // Recurrence rule: 'daily' | 'weekly:1'..'weekly:7' | 'monthly:1'..'monthly:28'
  // | 'every:{n}{d|w}@{YYYY-MM-DD}'. Set on hidden TEMPLATE rows (is_template=true)
  // and COPIED onto each spawned instance so a card can label its own cadence.
  recurrence?: string | null;
  is_template?: boolean;
  // Instance → the template it was spawned from (NULL on one-off tasks).
  template_id?: number | null;
  // Periods that came due while this instance was still open. Only the older
  // template→instance model writes this; self-renewing cards leave it at 0.
  overdue_periods?: number | null;
  // Response-only (PUT /:id/status): set when completing a 週期任務 rescheduled it
  // instead of closing it. Value is the next due date, 'YYYY-MM-DD'.
  recurring_next_due?: string | null;
  // Everyone the task is assigned to. `assignee_id`/`assignee_name` above are the
  // FIRST of these, kept by the backend for older readers — render from this.
  assignee_ids?: number[] | null;
  // Present on the POST /api/tasks response when a template was created and an
  // instance was spawned for today (1 = spawned).
  spawned_today?: number;
}

export interface Employee {
  id: number;
  name: string;
  email?: string;
}

interface TaskComment {
  id: number;
  task_id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  comment: string;
  created_at: string;
  mentions?: number[];
}

// Attachment row shape follows the backend contract. Field access is kept tolerant
// (several candidate names) so the UI does not break on minor naming differences.
interface TaskAttachment {
  id: number;
  task_id?: number;
  filename?: string;
  mime_type?: string | null;
  size_bytes?: number; // backend field name
  size?: number;       // tolerated fallback
  uploaded_by?: number | null;
  uploaded_by_name?: string | null;
  uploader?: string;   // tolerated fallback
  created_at?: string;
}

interface TaskDetailDrawerProps {
  open: boolean;
  task: Task | null;
  employees: Employee[];
  onClose: () => void;
  onTaskUpdated: (task: Task) => void;
  // Remove the task outright (parent confirms + calls the API + closes the drawer).
  onDelete: (task: Task) => void;
}

const priorityLabels: Record<string, string> = { low: '低', medium: '中', high: '高' };
const priorityColors: Record<string, 'success' | 'warning' | 'error'> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
};
const priorities: Array<'low' | 'medium' | 'high'> = ['low', 'medium', 'high'];

// Platform pickers offer only these two; legacy values (Shopee/Shopify/…) are
// still rendered by prepending the current value when it is not one of these.
const PLATFORM_OPTIONS: string[] = ['官網', '蝦皮'];

// Active pull-based board columns mapped to the backend `board_section` values.
// 已完成 is derived from status, so it is not offered here.
const BOARD_SECTIONS: Array<{ key: string; label: string }> = [
  { key: 'todo', label: '待辦' },
  { key: 'doing', label: '進行中' },
  { key: 'waiting', label: '等待中' },
  { key: 'review', label: '待審核' },
];

// Format a Date as local 'YYYY-MM-DD' (avoids the UTC day-shift from toISOString).
const toISODate = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value?: string | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleString('zh-TW', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Human-readable file size.
const formatSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15MB per the contract

const attName = (a: TaskAttachment): string =>
  a.filename || `附件 ${a.id}`;
const attSize = (a: TaskAttachment): number | undefined =>
  a.size_bytes ?? a.size;
const attUploader = (a: TaskAttachment): string =>
  a.uploaded_by_name || a.uploader || '';
const attDate = (a: TaskAttachment): string | undefined =>
  a.created_at;

const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
  open,
  task,
  employees,
  onClose,
  onTaskUpdated,
  onDelete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Working copy of the task; kept in sync when the parent hands over a new one.
  const [current, setCurrent] = useState<Task | null>(task);

  // Title inline editing.
  const [editingTitle, setEditingTitle] = useState<boolean>(false);
  const [titleDraft, setTitleDraft] = useState<string>('');

  // Description editing (save button appears only when dirty).
  const [descDraft, setDescDraft] = useState<string>('');
  const [savingDesc, setSavingDesc] = useState<boolean>(false);

  // Comments.
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [newComment, setNewComment] = useState<string>('');
  const [postingComment, setPostingComment] = useState<boolean>(false);

  // @mention suggester (composer).
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const commentBoxRef = useRef<HTMLDivElement | null>(null);
  const [mentionOpen, setMentionOpen] = useState<boolean>(false);
  const [mentionQuery, setMentionQuery] = useState<string>('');
  const [mentionStart, setMentionStart] = useState<number>(-1);
  // ids the user inserted via the suggester; the authoritative mentions list at
  // submit time is these ∩ '@{name}' strings still present in the final text.
  const [pendingMentions, setPendingMentions] = useState<Set<number>>(new Set());

  // Attachments.
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [attLoading, setAttLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const [savingMeta, setSavingMeta] = useState<boolean>(false);

  const taskId = task?.id;

  // Sync internal state whenever a different task is opened.
  useEffect(() => {
    setCurrent(task);
    setEditingTitle(false);
    setTitleDraft(task?.title ?? '');
    setDescDraft(task?.description ?? '');
    setNewComment('');
    setMentionOpen(false);
    setMentionQuery('');
    setMentionStart(-1);
    setPendingMentions(new Set());
  }, [task]);

  const fetchComments = useCallback(async (id: number): Promise<void> => {
    setCommentsLoading(true);
    try {
      const res = await api.get<TaskComment[]>(`/api/tasks/${id}/comments`);
      // Backend returns newest-first; show oldest-first so the thread reads top→bottom.
      setComments([...res.data].reverse());
    } catch (error) {
      console.error('Error fetching comments:', error);
      enqueueSnackbar('無法載入留言', { variant: 'error' });
    } finally {
      setCommentsLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchAttachments = useCallback(async (id: number): Promise<void> => {
    setAttLoading(true);
    try {
      const res = await api.get<TaskAttachment[]>(`/api/tasks/${id}/attachments`);
      setAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      // Endpoint may still be rolling out; fail soft with an empty list.
      setAttachments([]);
    } finally {
      setAttLoading(false);
    }
  }, []);

  // Load comments + attachments when the drawer opens for a task.
  useEffect(() => {
    if (open && taskId) {
      fetchComments(taskId);
      fetchAttachments(taskId);
    } else {
      setComments([]);
      setAttachments([]);
    }
  }, [open, taskId, fetchComments, fetchAttachments]);

  // Build a FULL field snapshot for PUT /api/tasks/:id. The backend assigns
  // assignee_id directly (not COALESCE), so a partial payload would wipe the
  // assignee — always send the current field set with the given overrides applied.
  const saveTaskFields = async (
    overrides: Partial<{
      title: string;
      description: string;
      platform: string;
      priority: 'low' | 'medium' | 'high';
      assignee_id: number | null;
      assignee_ids: number[]; // full list; [] = 未指派
      due_date: string; // 'YYYY-MM-DD' or '' to leave unchanged
      recurrence: string; // rule, or '' to stop recurring
    }>,
    setBusy?: (v: boolean) => void
  ): Promise<void> => {
    if (!current) return;
    if (setBusy) setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', overrides.title ?? current.title);
      fd.append('description', overrides.description ?? current.description ?? '');
      // platform is optional: append only when non-empty (empty → backend stores NULL).
      const platform = overrides.platform !== undefined ? overrides.platform : (current.platform ?? '');
      if (platform) fd.append('platform', platform);
      fd.append('priority', overrides.priority ?? current.priority ?? 'medium');
      fd.append('tags', JSON.stringify(current.tags ?? []));

      // Assignment: always send the full list as JSON. The backend treats the
      // field's presence as "assignment provided" and mirrors the first id into
      // assignee_id, so an empty array is how 未指派 is expressed.
      const ids =
        overrides.assignee_ids !== undefined
          ? overrides.assignee_ids
          : overrides.assignee_id !== undefined
            ? (overrides.assignee_id === null ? [] : [overrides.assignee_id])
            : assigneeIdsOf(current);
      fd.append('assignee_ids', JSON.stringify(ids));

      // due_date: send the chosen date, else keep the current one. Backend COALESCEs,
      // so an empty value leaves the existing due date untouched.
      const due = overrides.due_date !== undefined ? overrides.due_date : (current.due_date ?? '');
      if (due) fd.append('due_date', due);

      // recurrence: only sent when this save is changing it. Absent = unchanged;
      // '' = stop recurring. Never echoed back on unrelated saves, so a title
      // edit can't resurrect a rule the user just cleared elsewhere.
      if (overrides.recurrence !== undefined) {
        fd.append('recurrence', overrides.recurrence);
      }

      const res = await api.put<Task>(`/api/tasks/${current.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCurrent(res.data);
      onTaskUpdated(res.data);
    } catch (error) {
      console.error('Error saving task:', error);
      enqueueSnackbar('儲存失敗', { variant: 'error' });
      throw error;
    } finally {
      if (setBusy) setBusy(false);
    }
  };

  // --- Title ---
  const commitTitle = async (): Promise<void> => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!current || !next || next === current.title) {
      setTitleDraft(current?.title ?? '');
      return;
    }
    try {
      await saveTaskFields({ title: next });
      enqueueSnackbar('標題已更新', { variant: 'success' });
    } catch {
      setTitleDraft(current.title);
    }
  };

  // --- Description ---
  const descDirty = !!current && descDraft !== (current.description ?? '');
  const commitDescription = async (): Promise<void> => {
    if (!current || !descDirty) return;
    try {
      await saveTaskFields({ description: descDraft }, setSavingDesc);
      enqueueSnackbar('描述已更新', { variant: 'success' });
    } catch {
      /* handled in saveTaskFields */
    }
  };

  // --- Meta: status / assignee / priority / due date ---
  const handleToggleStatus = async (): Promise<void> => {
    if (!current) return;
    const newStatus = current.status === 'pending' ? 'completed' : 'pending';
    setSavingMeta(true);
    try {
      const res = await api.put<Task>(`/api/tasks/${current.id}/status`, { status: newStatus });
      setCurrent(res.data);
      onTaskUpdated(res.data);
      enqueueSnackbar(newStatus === 'completed' ? '任務已完成' : '任務已重新開啟', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('更新狀態失敗', { variant: 'error' });
    } finally {
      setSavingMeta(false);
    }
  };

  // A task can have several 負責人. assignee_ids is the list; assignee_id mirrors
  // its first entry server-side, so read the list and fall back for older rows
  // that predate the column.
  const assigneeIdsOf = (t: Task | null): number[] => {
    if (!t) return [];
    if (Array.isArray(t.assignee_ids)) return t.assignee_ids;
    return t.assignee_id ? [t.assignee_id] : [];
  };

  const employeeName = (id: number): string =>
    employees.find((e) => e.id === id)?.name ?? `#${id}`;

  const handleAssigneesChange = (e: SelectChangeEvent<number[]>): void => {
    const raw = e.target.value;
    const ids = (typeof raw === 'string' ? raw.split(',').map(Number) : raw).filter(
      (n) => !Number.isNaN(n)
    );
    saveTaskFields({ assignee_ids: ids })
      .then(() => enqueueSnackbar('指派已更新', { variant: 'success' }))
      .catch(() => {});
  };

  const handlePriorityChange = (e: SelectChangeEvent): void => {
    saveTaskFields({ priority: e.target.value as 'low' | 'medium' | 'high' })
      .then(() => enqueueSnackbar('優先級已更新', { variant: 'success' }))
      .catch(() => {});
  };

  const handleDueChange = (v: Date | null): void => {
    saveTaskFields({ due_date: toISODate(v) })
      .then(() => enqueueSnackbar('截止日期已更新', { variant: 'success' }))
      .catch(() => {});
  };

  const handlePlatformChange = (e: SelectChangeEvent): void => {
    saveTaskFields({ platform: e.target.value })
      .then(() => enqueueSnackbar('平台已更新', { variant: 'success' }))
      .catch(() => {});
  };

  // --- 重複 (週期任務 cadence) ---
  // This is where a routine's interval gets changed: open the card, set 每 N 天/週.
  // Changing the UNIT saves at once (it's a complete choice); changing the NUMBER
  // saves on blur/Enter so a half-typed "1" of "12" is never written.
  const recurControl = ruleToControl(current?.recurrence);
  const [recurN, setRecurN] = useState<number>(recurControl.n);
  const [recurUnit, setRecurUnit] = useState<RecurUnit>(recurControl.unit);

  // Re-seed the control whenever a different card is opened (or its rule changes
  // elsewhere, e.g. a drag into the 週期 column).
  useEffect(() => {
    const c = ruleToControl(current?.recurrence);
    setRecurN(c.n);
    setRecurUnit(c.unit);
  }, [current?.id, current?.recurrence]);

  const saveRecurrence = (rule: string): void => {
    if (rule === (current?.recurrence ?? '')) return;
    saveTaskFields({ recurrence: rule })
      .then(() =>
        enqueueSnackbar(rule ? `已設為${recurrenceLabel(rule)}提醒` : '已取消重複', {
          variant: 'success',
        })
      )
      .catch(() => {});
  };

  const handleRecurUnitChange = (e: SelectChangeEvent): void => {
    const unit = e.target.value as RecurUnit;
    const n = clampInterval(recurN, unit);
    setRecurUnit(unit);
    setRecurN(n);
    saveRecurrence(composeRule(unit, n));
  };

  const commitRecurN = (): void => {
    if (recurUnit === '') return;
    saveRecurrence(composeRule(recurUnit, recurN));
  };

  // Board section moves go through the dedicated PUT /:id/section endpoint.
  // Optimistic update; revert on failure.
  const handleSectionChange = async (e: SelectChangeEvent): Promise<void> => {
    if (!current) return;
    const next = e.target.value;
    const prevSection = current.board_section ?? 'todo';
    if (next === prevSection) return;
    const optimistic = { ...current, board_section: next };
    setCurrent(optimistic);
    onTaskUpdated(optimistic);
    try {
      const res = await api.put<Task>(`/api/tasks/${current.id}/section`, { board_section: next });
      setCurrent(res.data);
      onTaskUpdated(res.data);
      enqueueSnackbar('區段已更新', { variant: 'success' });
    } catch (error) {
      console.error('Error updating section:', error);
      const reverted = { ...current, board_section: prevSection };
      setCurrent(reverted);
      onTaskUpdated(reverted);
      enqueueSnackbar('更新區段失敗', { variant: 'error' });
    }
  };

  // Platform select options: the two canonical values, plus the current value if
  // it is a legacy one so the Select renders it instead of showing blank.
  const platformOptions =
    current && current.platform && !PLATFORM_OPTIONS.includes(current.platform)
      ? [current.platform, ...PLATFORM_OPTIONS]
      : PLATFORM_OPTIONS;

  // --- Attachments ---
  const handleFileSelected = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0 || !current) return;
    const file = files[0];
    if (file.size > MAX_ATTACHMENT_BYTES) {
      enqueueSnackbar('檔案大小不能超過 15MB', { variant: 'warning' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post(`/api/tasks/${current.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      enqueueSnackbar('附件已上傳', { variant: 'success' });
      await fetchAttachments(current.id);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      enqueueSnackbar('上傳附件失敗', { variant: 'error' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Download via the axios instance so the Bearer token is attached; a plain
  // <a href> would not carry auth. Works whether the endpoint streams the file
  // or redirects to a signed URL (XHR follows the redirect transparently).
  const handleDownload = async (att: TaskAttachment): Promise<void> => {
    if (!current) return;
    try {
      const res = await api.get(`/api/tasks/${current.id}/attachments/${att.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attName(att);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      enqueueSnackbar('下載附件失敗', { variant: 'error' });
    }
  };

  const handleDeleteAttachment = async (att: TaskAttachment): Promise<void> => {
    if (!current) return;
    if (!window.confirm(`確定要刪除附件「${attName(att)}」嗎？`)) return;
    try {
      await api.delete(`/api/tasks/${current.id}/attachments/${att.id}`);
      enqueueSnackbar('附件已刪除', { variant: 'success' });
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
    } catch (error) {
      console.error('Error deleting attachment:', error);
      enqueueSnackbar('刪除附件失敗', { variant: 'error' });
    }
  };

  // --- Comments: @mention suggester ---
  const closeMention = (): void => {
    setMentionOpen(false);
    setMentionQuery('');
    setMentionStart(-1);
  };

  // Detect an in-progress '@name' token ending at the caret and open the suggester.
  const detectMention = (value: string, caret: number): void => {
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf('@');
    if (at === -1) {
      closeMention();
      return;
    }
    const between = upToCaret.slice(at + 1);
    // Whitespace after '@' (no match yet) ends the token → close.
    if (/\s/.test(between)) {
      closeMention();
      return;
    }
    // Only trigger when '@' starts a word (start of text or after whitespace), so
    // things like email@domain don't open the menu.
    const prev = at > 0 ? upToCaret[at - 1] : '';
    if (prev && !/\s/.test(prev)) {
      closeMention();
      return;
    }
    setMentionStart(at);
    setMentionQuery(between);
    setMentionOpen(true);
  };

  const handleCommentChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const value = e.target.value;
    setNewComment(value);
    detectMention(value, e.target.selectionStart ?? value.length);
  };

  // Replace the '@query' token with '@{name} ' and record the mentioned id.
  const insertMention = (emp: Employee): void => {
    if (mentionStart < 0) {
      closeMention();
      return;
    }
    const before = newComment.slice(0, mentionStart);
    const after = newComment.slice(mentionStart + 1 + mentionQuery.length);
    const insert = `@${emp.name} `;
    const next = before + insert + after;
    setNewComment(next);
    setPendingMentions((prev) => new Set(prev).add(emp.id));
    closeMention();
    // Restore focus + place the caret right after the inserted mention.
    const caret = before.length + insert.length;
    requestAnimationFrame(() => {
      const el = commentInputRef.current;
      if (el) {
        el.focus();
        try {
          el.setSelectionRange(caret, caret);
        } catch {
          /* ignore */
        }
      }
    });
  };

  // --- Comments ---
  const handlePostComment = async (): Promise<void> => {
    const text = newComment.trim();
    if (!text || !current) return;
    // Authoritative mentions: inserted ids whose '@{name}' string is still present
    // (the user may have deleted one after inserting).
    const mentions = employees
      .filter((e) => pendingMentions.has(e.id) && text.includes(`@${e.name}`))
      .map((e) => e.id);
    setPostingComment(true);
    try {
      const res = await api.post<TaskComment>(`/api/tasks/${current.id}/comments`, {
        comment: text,
        mentions,
      });
      setComments((prev) => [...prev, res.data]);
      setNewComment('');
      setPendingMentions(new Set());
      closeMention();
    } catch (error) {
      console.error('Error posting comment:', error);
      enqueueSnackbar('送出留言失敗', { variant: 'error' });
    } finally {
      setPostingComment(false);
    }
  };

  // Render a comment body, highlighting every '@{name}' that matches an employee.
  const renderCommentBody = (text: string): React.ReactNode => {
    const names = employees.map((e) => e.name).filter(Boolean);
    if (names.length === 0) return text;
    // Longest names first so overlapping prefixes match the fuller name.
    const sorted = [...names].sort((a, b) => b.length - a.length);
    const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`@(?:${sorted.map(escape).join('|')})`, 'g');
    const nodes: React.ReactNode[] = [];
    let last = 0;
    let key = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) nodes.push(text.slice(last, m.index));
      nodes.push(
        <Box component="span" key={`m${key++}`} sx={{ color: 'primary.main', fontWeight: 600 }}>
          {m[0]}
        </Box>
      );
      last = m.index + m[0].length;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return nodes;
  };

  const mentionMatches = mentionOpen
    ? employees
        .filter((e) => e.name.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 8)
    : [];

  const completed = current?.status === 'completed';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, maxWidth: '100%' } }}
    >
      {current && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header: status toggle + close */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Button
              size="small"
              variant={completed ? 'contained' : 'outlined'}
              color="success"
              startIcon={
                savingMeta ? (
                  <CircularProgress size={16} color="inherit" />
                ) : completed ? (
                  <CheckCircleIcon />
                ) : (
                  <PendingIcon />
                )
              }
              onClick={handleToggleStatus}
              disabled={savingMeta}
            >
              {completed ? '已完成 · 重新開啟' : '標記完成'}
            </Button>
            <IconButton
              onClick={() => task && onDelete(task)}
              aria-label="刪除任務"
              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton onClick={onClose} aria-label="關閉">
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Title (inline-editable) */}
            {editingTitle ? (
              <TextField
                fullWidth
                autoFocus
                variant="standard"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                  if (e.key === 'Escape') {
                    setTitleDraft(current.title);
                    setEditingTitle(false);
                  }
                }}
                InputProps={{ sx: { fontSize: '1.4rem', fontWeight: 600 } }}
              />
            ) : (
              <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    cursor: 'text',
                    flex: 1,
                    textDecoration: completed ? 'line-through' : 'none',
                    color: completed ? 'text.secondary' : 'text.primary',
                  }}
                  onClick={() => {
                    setTitleDraft(current.title);
                    setEditingTitle(true);
                  }}
                >
                  {current.title}
                </Typography>
                <Tooltip title="編輯標題">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTitleDraft(current.title);
                      setEditingTitle(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}

            {/* Meta row: assignee / due / priority */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={2} sx={{ mt: 3 }}>
                {/* 負責人 — multi-select. Empty selection = 未指派. */}
                <FormControl fullWidth size="small">
                  <InputLabel>負責人</InputLabel>
                  <Select
                    label="負責人"
                    multiple
                    value={assigneeIdsOf(current)}
                    onChange={handleAssigneesChange}
                    renderValue={(selected) =>
                      (selected as number[]).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">未指派</Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(selected as number[]).map((id) => (
                            <Chip key={id} size="small" label={employeeName(id)} />
                          ))}
                        </Stack>
                      )
                    }
                  >
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        <Checkbox
                          size="small"
                          checked={assigneeIdsOf(current).includes(emp.id)}
                        />
                        {emp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DatePicker
                  label="截止日期"
                  value={parseDate(current.due_date)}
                  onChange={handleDueChange}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>優先級</InputLabel>
                  <Select label="優先級" value={current.priority} onChange={handlePriorityChange}>
                    {priorities.map((p) => (
                      <MenuItem key={p} value={p}>
                        <Chip label={priorityLabels[p]} size="small" color={priorityColors[p]} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>平台</InputLabel>
                  <Select label="平台" value={current.platform ?? ''} onChange={handlePlatformChange}>
                    <MenuItem value="">不指定</MenuItem>
                    {platformOptions.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>區段</InputLabel>
                  <Select
                    label="區段"
                    value={BOARD_SECTIONS.some((s) => s.key === current.board_section)
                      ? current.board_section
                      : 'todo'}
                    onChange={handleSectionChange}
                  >
                    {BOARD_SECTIONS.map((s) => (
                      <MenuItem key={s.key} value={s.key}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* 重複 — where a routine's interval is changed. Any number. */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 32 }}>重複</Typography>
                  <TextField
                    size="small"
                    label="每"
                    type="number"
                    value={recurN}
                    disabled={recurUnit === ''}
                    onChange={(e) => setRecurN(clampInterval(e.target.value, recurUnit))}
                    onBlur={commitRecurN}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    inputProps={{ min: 1, max: recurUnit === 'w' ? 52 : 365 }}
                    sx={{ width: 90 }}
                  />
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>單位</InputLabel>
                    <Select label="單位" value={recurUnit} onChange={handleRecurUnitChange}>
                      {RECUR_UNITS.map((u) => (
                        <MenuItem key={u.value || 'none'} value={u.value}>
                          {u.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                {current.recurrence && (
                  <Typography variant="caption" color="text.secondary">
                    留在「週期」欄提醒 · {recurrenceLabel(current.recurrence)}
                    {nextOccurrence(current.due_date, current.recurrence)
                      ? ` · 下次 ${nextOccurrence(current.due_date, current.recurrence)!.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' })}`
                      : ' · 設定截止日期即可顯示下次提醒時間'}
                  </Typography>
                )}
              </Stack>
            </LocalizationProvider>

            <Divider sx={{ my: 3 }} />

            {/* Description */}
            <Typography variant="subtitle2" gutterBottom>
              描述
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="輸入任務細節…"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
            />
            {descDirty && (
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setDescDraft(current.description ?? '')}
                  disabled={savingDesc}
                >
                  取消
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={commitDescription}
                  disabled={savingDesc}
                  startIcon={savingDesc ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  儲存描述
                </Button>
              </Stack>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Attachments */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">附件</Typography>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelected(e.target.files)}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />
                }
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                上傳附件
              </Button>
            </Stack>
            {attLoading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={48} />
              </Stack>
            ) : attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                尚無附件
              </Typography>
            ) : (
              <List dense disablePadding>
                {attachments.map((att) => (
                  <ListItem
                    key={att.id}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="下載">
                          <IconButton edge="end" size="small" onClick={() => handleDownload(att)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="刪除">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAttachment(att)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <AttachFileIcon color="action" />
                    </ListItemAvatar>
                    <ListItemText
                      primary={attName(att)}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { wordBreak: 'break-all', pr: 6 },
                      }}
                      secondary={
                        [formatSize(attSize(att)), attUploader(att), formatDateTime(attDate(att))]
                          .filter(Boolean)
                          .join(' · ')
                      }
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Comments */}
            <Typography variant="subtitle2" gutterBottom>
              留言
            </Typography>
            {commentsLoading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
              </Stack>
            ) : comments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                尚無留言
              </Typography>
            ) : (
              <List disablePadding>
                {comments.map((c) => (
                  <ListItem key={c.id} alignItems="flex-start" disableGutters sx={{ px: 0 }}>
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {(c.user_name || '?').charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" fontWeight={600}>
                            {c.user_name || '使用者'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(c.created_at)}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                        >
                          {renderCommentBody(c.comment)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* Comment composer */}
            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: 2 }}>
              <Box ref={commentBoxRef} sx={{ flex: 1, position: 'relative' }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder="新增留言…（輸入 @ 提及同事）"
                  value={newComment}
                  inputRef={commentInputRef}
                  onChange={handleCommentChange}
                  onKeyDown={(e) => {
                    // While the suggester is open, Escape closes it and Enter picks
                    // the sole match — before the Cmd/Ctrl+Enter submit shortcut.
                    if (mentionOpen && mentionMatches.length > 0) {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        closeMention();
                        return;
                      }
                      if (e.key === 'Enter' && mentionMatches.length === 1) {
                        e.preventDefault();
                        insertMention(mentionMatches[0]);
                        return;
                      }
                    }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handlePostComment();
                    }
                  }}
                />
                <Popper
                  open={mentionOpen && mentionMatches.length > 0}
                  anchorEl={commentBoxRef.current}
                  placement="top-start"
                  style={{ zIndex: 1400, width: commentBoxRef.current?.clientWidth }}
                >
                  <ClickAwayListener onClickAway={closeMention}>
                    <Paper elevation={3} sx={{ maxHeight: 200, overflowY: 'auto', mb: 0.5 }}>
                      <MenuList dense>
                        {mentionMatches.map((emp) => (
                          <MenuItem key={emp.id} onClick={() => insertMention(emp)}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, mr: 1 }}>
                              {emp.name.charAt(0)}
                            </Avatar>
                            {emp.name}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </Box>
              <Button
                variant="contained"
                onClick={handlePostComment}
                disabled={postingComment || !newComment.trim()}
                sx={{ minWidth: 0, px: 2 }}
              >
                {postingComment ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default TaskDetailDrawer;
